import React, { useEffect, useRef, useState } from "react";
import SupervisorManager from "../managers/SupervisorManager";
import { useMsal } from "@azure/msal-react";
import { Supervisor, SupervisorCount, SupervisorListResponse, SupervisorListPageProps, Approver, ApproverListResponse } from "../interfaces/SupervisorInterface";
import "./SupervisorListPage.css";
import "./EmployeeView.css";
import Colors from '../styles/Colors.json';
import { useSiteContext } from "../SiteContext";
import { Page } from "../interfaces/PagesInterface";
import PageWrapper from "../components/PageWrapper";
import SearchBar from "../components/SearchBar"; // Import the SearchBar component
import { printTimesheetBatch, printTimesheetBatchAll } from "../util/PrintUtil";
import ActionButton from "../components/ActionButton";
import { TimesheetType } from "../interfaces/TimesheetInterface";
import config from "../configLoader";
import VistaBatchExportModal from "../components/VistaBatchExportModal";
import VistaBatchExportV2Modal from "../components/VistaBatchExportV2Modal";
import { sendUnsubmittedNotification } from "../api/notifications";
import { generateSageExport, pushSageRows } from "../api/2020ph";
import { downloadPayrollApprovedSummaryReport } from "../api/payrollApprovedSummaryReport";
import { can } from "../util/adminPermissions";
import { JobStatus } from "../interfaces/V2Interface";

const SupervisorListPage: React.FC<SupervisorListPageProps> = ({ setError }) => {
  const { payPeriodID, setSuperName, setSuperID, type, setPage, loading, setEmployee, setLoading, setTimesheetID, translator, setSuperAltID, superID, superAltID, payPeriods, userRole, userID, startDate, endDate, createPopup, locations } = useSiteContext();

  // State to store fetched supervisor/approver data and loading status
  const [data, setData] = useState<SupervisorListResponse | ApproverListResponse | null>(null);
  const [searchTerm, setSearchTerm] = useState<string>(""); // State to manage the search term
  // Initialize selectedLocation from localStorage, defaulting to "ALL" if not found
  const [selectedLocation, setSelectedLocation] = useState<string>(() => {
    const savedLocation = localStorage.getItem('supervisorListSelectedLocation');
    return savedLocation || "ALL";
  });
  const [isDownloading, setIsDownloading] = useState<boolean>(false);
  const [isSageExporting, setIsSageExporting] = useState(false);
  const [isSummaryDownloading, setIsSummaryDownloading] = useState(false);
  const [summaryProgress, setSummaryProgress] = useState<{ progress: number; currentStep: string }>({
    progress: 0,
    currentStep: "Job queued...",
  });
  const [isPushingToSage, setIsPushingToSage] = useState(false);
  const [sagePushConfirmVisible, setSagePushConfirmVisible] = useState(false);
  const [pushNotificationSent, setPushNotificationSent] = useState<boolean>(false); // State to track if push notification was sent

  // Modal state for Vista export
  const [vistaModalVisible, setVistaModalVisible] = useState<boolean>(false);
  const [vistaV2ModalVisible, setVistaV2ModalVisible] = useState<boolean>(false);
  const [selectedSupervisorName, setSelectedSupervisorName] = useState<string>("");
  const [selectedSupervisorId, setSelectedSupervisorId] = useState<string>("");
  const [selectedSupervisorAltId, setSelectedSupervisorAltId] = useState<string>("");
  const [batchAllProgress, setBatchAllProgress] = useState<{ progress: number; currentStep: string }>({
    progress: 0,
    currentStep: "Job queued...",
  });
  const downloadAllAbortControllerRef = useRef<AbortController | null>(null);

  // MSAL instance for Azure authentication
  const { instance, accounts } = useMsal();

  // Fetch the supervisor/approver data when the component mounts or when parameters change
  useEffect(() => {
    const fetchData = async () => {
      // Return early if necessary parameters are not available
      if (!payPeriodID || !type) return;

      // Set loading state to true before fetching data
      setLoading(true);

      try {
        const adminUserID = userRole.admin || userRole.payroll || userRole.hr ? undefined : userID;
        // Fetch data from the API using the msal instance, accounts, payPeriodID, and type
        const response = await SupervisorManager.getSupervisors(payPeriodID, type, adminUserID);
        
        // Set the fetched data into state
        setData(response.data);
      } catch (error) {
        // Handle any errors that occur during data fetching
        console.error("Error fetching data:", error);
        setError("Failed to fetch data. Please try again later.");
      } finally {
        // Stop loading after data fetch attempt is finished
        setLoading(false);
      }
    };

    fetchData();
  }, [payPeriodID, type, instance, accounts, setError, setLoading]);

  useEffect(() => {
    return () => {
      downloadAllAbortControllerRef.current?.abort();
    };
  }, []);


  // Handle download action
  const handleDownload = async (supervisorID: number, altSupervisorID: string) => {
    if (!payPeriodID) return;
    const payPeriodObj = payPeriods.find(pp => pp.PayPeriodID === payPeriodID);
    setIsDownloading(true);
    setLoading(true);
    try {
        await printTimesheetBatch(supervisorID, payPeriodID, altSupervisorID, payPeriodObj);
    } catch (error) {
        console.error("Error downloading timecard batch:", error);
        setError("Failed to download timecards. Please try again later.");
    } finally {
        setIsDownloading(false);
        setLoading(false);
    }
  };

  // Handle download all action
  const handleDownloadAll = async () => {
    if (!payPeriodID) return;
    const payPeriodObj = payPeriods.find(pp => pp.PayPeriodID === payPeriodID);
    downloadAllAbortControllerRef.current?.abort();
    const controller = new AbortController();
    downloadAllAbortControllerRef.current = controller;

    let locationID: number | undefined;
    let locationLabel: string | undefined;
    if (selectedLocation !== "ALL") {
      const loc = locations?.find(l => l.locationID === parseInt(selectedLocation));
      if (loc) {
        locationID = loc.locationID;
        locationLabel = loc.altLocationID || loc.name;
      }
    }

    setIsDownloading(true);
    setBatchAllProgress({ progress: 0, currentStep: "Job queued..." });
    try {
      await printTimesheetBatchAll(
        payPeriodID,
        payPeriodObj,
        controller.signal,
        (jobStatus: JobStatus) => {
          const progress = typeof jobStatus.progress === "number" ? Math.max(0, Math.min(100, jobStatus.progress)) : 0;
          setBatchAllProgress({
            progress,
            currentStep: jobStatus.currentStep || "Processing...",
          });
        },
        locationID,
        locationLabel
      );
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : "Failed to download timecards. Please try again later.";
      createPopup("Error", errorMessage);
    } finally {
      if (downloadAllAbortControllerRef.current === controller) {
        downloadAllAbortControllerRef.current = null;
      }
      setIsDownloading(false);
    }
  };

  const handleSagePush = () => {
    if (!payPeriodID) return;
    setSagePushConfirmVisible(true);
  };

  const confirmSagePush = async () => {
    if (!payPeriodID) return;
    setSagePushConfirmVisible(false);
    setIsPushingToSage(true);
    try {
      const result = await pushSageRows(payPeriodID);
      createPopup("Sage API Export Complete", `${result.rowCount} rows successfully pushed to Sage`);
    } catch (error: any) {
      // pushSageRows throws SageExportError with the API's full text (incl. the 422
      // hour-reconciliation listing) already decoded into message.
      const msg = error instanceof Error ? error.message : "Failed to push rows to Sage.";
      createPopup("Export to Sage API Failed", msg);
    } finally {
      setIsPushingToSage(false);
    }
  };

  const handleSageExport = async () => {
    if (!payPeriodID) return;
    const payPeriodObj = payPeriods.find(pp => pp.PayPeriodID === payPeriodID);
    const fileName = payPeriodObj
        ? `SagePayroll-${payPeriodObj.AccountingYear}-Week${payPeriodObj.WeekNumber}.xlsx`
        : "SageExport.xlsx";
    setIsSageExporting(true);
    try {
        await generateSageExport(payPeriodID, fileName);
    } catch (error) {
        const msg = error instanceof Error ? error.message : "Failed to generate Sage export.";
        createPopup("Sage Export Blocked", msg);
    } finally {
        setIsSageExporting(false);
    }
  };

  const handlePayrollSummaryDownload = async () => {
    if (!payPeriodID) return;
    const payPeriodObj = payPeriods.find(pp => pp.PayPeriodID === payPeriodID);
    const fileName = payPeriodObj
        ? `PayrollApprovedSummary_Week${payPeriodObj.WeekNumber}_${payPeriodObj.AccountingYear}.xlsx`
        : `PayrollApprovedSummary_${payPeriodID}.xlsx`;
    setSummaryProgress({ progress: 0, currentStep: "Job queued..." });
    setIsSummaryDownloading(true);
    try {
        await downloadPayrollApprovedSummaryReport(payPeriodID, fileName, (jobStatus) =>
          setSummaryProgress({ progress: jobStatus.progress ?? 0, currentStep: jobStatus.currentStep || "Processing..." })
        );
    } catch (error) {
        const msg = error instanceof Error ? error.message : "Failed to download payroll approved summary report.";
        createPopup("Error", msg);
    } finally {
        setIsSummaryDownloading(false);
    }
  };

  // Handle push notification reminder action
  const handlePushNotificationReminder = async () => {
    if (!payPeriodID) {
      createPopup("Error", "PayPeriodID is required to send notifications.");
      return;
    }

    setLoading(true);
    try {
      const response = await sendUnsubmittedNotification(payPeriodID);
      
      if (response.success) {
        createPopup("Success", response.message);
        setPushNotificationSent(true);
      } else {
        createPopup("Error", response.message || "Failed to send notification.");
      }
    } catch (error) {
      console.error("Error sending push notification:", error);
      const errorMessage = error instanceof Error ? error.message : "An unexpected error occurred while sending notifications.";
      createPopup("Error", errorMessage);
    } finally {
      setLoading(false);
    }
  };

  // Handle Vista export action
  const handleVistaExport = (supervisorID: number, supervisorName: string, altSupervisorID: string) => {
    setSelectedSupervisorName(supervisorName);
    setSelectedSupervisorId(supervisorID.toString());
    setSelectedSupervisorAltId(altSupervisorID);
    setVistaModalVisible(true);
  };

  // Handle Vista export V2 action (admin only)
  const handleVistaExportV2 = (supervisorID: number, supervisorName: string, altSupervisorID: string) => {
    setSelectedSupervisorName(supervisorName);
    setSelectedSupervisorId(supervisorID.toString());
    setSelectedSupervisorAltId(altSupervisorID);
    setVistaV2ModalVisible(true);
  };

  // Handle supervisor card click to navigate to the employee list page
  const handleClick = (supID: number, supName: string, supAltID: string) => {
    console.log(supID);
    if (supID === null) {
      setSuperID(0);
    } else {
      setSuperID(supID);
    }
    if (supAltID === null) {
      setSuperAltID(superID.toString());
    } else {
      setSuperAltID(supAltID);
    }
    setSuperName(supName);
    setPage(Page.EmployeeList);
  };

  // Handle location change and persist to localStorage
  const handleLocationChange = (location: string) => {
    setSelectedLocation(location);
    localStorage.setItem('supervisorListSelectedLocation', location);
  };

  // Handle approver card click to navigate to the employee list page
  const handleApproverClick = async (approver: Approver) => {
    try {
      setLoading(true);
      
      if (approver.type === "manager") {
        // Store manager information in context
        setSuperID(approver.employeeID || 0);
        setSuperAltID(`manager_${approver.employeeID}`); // Prefix to identify as manager
        setSuperName(getApproverDisplayName(approver));
        setPage(Page.EmployeeList);
      } else if (approver.type === "approvalGroup") {
        // Store approval group information in context
        setSuperID(approver.approvalGroupID || 0);
        setSuperAltID(`approvalGroup_${approver.approvalGroupID}`); // Prefix to identify as approval group
        setSuperName(getApproverDisplayName(approver));
        setPage(Page.EmployeeList);
      }
    } catch (error) {
      console.error("Error handling approver click:", error);
      setError("Failed to process approver selection. Please try again later.");
    } finally {
      setLoading(false);
    }
  };

  const handleEmployeeClick = async (employee: any) => {
    if (!payPeriodID) return;
    
    setLoading(true);
    
    try {
      let timesheetData;
      
      // Determine which API to call based on timesheet type
      if (type === TimesheetType.Unsubmitted) {
        timesheetData = await SupervisorManager.getUnsubmittedEmployeeTimesheet(
          employee.employeeID, 
          payPeriodID
        );
      } else {
        timesheetData = await SupervisorManager.getEmployeeTimesheet(
          employee.employeeID, 
          payPeriodID
        );
      }
      
      // Update the context with the full employee data including timesheet info
      setEmployee({
        ...employee,
        days: timesheetData.days || [],
        hourlyRate: timesheetData.hourlyRate,
        jobTitle: timesheetData.jobTitle,
        phoneNumber: timesheetData.phoneNumber,
        managerApprovals: timesheetData.managerApprovals || [],
        superApprovals: timesheetData.superApprovals || [],
        payrollApprovals: timesheetData.payrollApprovals || [],
        approvalGroups: timesheetData.approvalGroups || [],
        // Include any other necessary fields from the API response
      });

      if (timesheetData.timesheetID) { 
        setTimesheetID(timesheetData.timesheetID);
      } else {
        setTimesheetID(0);
      }
      
      // Set other required context values
      const supervisor = timesheetData.assignedSupervisor || timesheetData.timesheetSupervisor;

      setSuperID(supervisor.employeeID);
      setSuperAltID(supervisor.altEmployeeID || supervisor.employeeID.toString());
      setSuperName(`${supervisor.name.last} - ${supervisor.name.first}`);      
      
      // Navigate to TimesheetPage
      setPage(Page.TimeSheetPage);
    } catch (error) {
      console.error("Error fetching employee timesheet:", error);
      setError("Failed to load employee timesheet. Please try again.");
    } finally {
      setLoading(false);
    }
  };


  // Filter supervisors based on the search term and location
  const filteredSupervisors = (data as SupervisorListResponse)?.supervisors?.filter((supervisor) => {
    // Filter by location if a location is selected - check if any employee has matching locationID
    const matchesLocation = selectedLocation === "ALL" || 
      (selectedLocation !== "ALL" && supervisor.employees.some(employee => 
        employee.locationID !== undefined && employee.locationID === parseInt(selectedLocation)
      ));
    
    if (!matchesLocation) return false;

    const searchTermLower = searchTerm.toLowerCase(); // Convert search term to lowercase for case-insensitive comparison
    
    // Check if the search term contains letters (name search)
    const supervisorNameMatch = `${supervisor.name.first} ${supervisor.name.last}`.toLowerCase().includes(searchTermLower);
    const supervisorIdMatch = supervisor.altEmployeeID?.toLowerCase().includes(searchTermLower);
    
    // Check if any employee matches search
    const employeeMatch = supervisor.employees.some(employee => {
      const employeeNameMatch = `${employee.name.first} ${employee.name.last}`.toLowerCase().includes(searchTermLower);
      const employeeIdMatch = employee.altEmployeeID?.toLowerCase().includes(searchTermLower);
      return employeeNameMatch || employeeIdMatch;
    });

    return supervisorNameMatch || supervisorIdMatch || employeeMatch;
  }) || [];

  //alert(filteredSupervisors.length);

  // Filter approvers based on the search term and location
  const filteredApprovers = (data as ApproverListResponse)?.approvers?.filter((approver) => {
    // Filter by location if a location is selected - check if any employee has matching locationID
    // This applies to both approval groups and managers
    if (selectedLocation !== "ALL") {
      const matchesLocation = approver.employees?.some(employee => 
        employee.locationID !== undefined && employee.locationID === parseInt(selectedLocation)
      ) || false;
      
      if (!matchesLocation) {
        return false;
      }
    }

    // If no search term, show all approvers (after location filter)
    if (!searchTerm || searchTerm.trim() === '') {
      return true;
    }

    const searchTermLower = searchTerm.toLowerCase();
    
    // Check if the approver itself matches the search
    let approverMatch = false;
    if (/[a-zA-Z]/.test(searchTerm)) {
      if (approver.type === "approvalGroup") {
        approverMatch = approver.approvalGroupName?.toLowerCase().includes(searchTermLower) || false;
      } else if (approver.type === "manager" && approver.name) {
        const fullName = `${approver.name.first} ${approver.name.last}`.toLowerCase();
        approverMatch = fullName.includes(searchTermLower);
      }
    } else {
      if (approver.type === "manager" && approver.altEmployeeID) {
        approverMatch = approver.altEmployeeID.toString().includes(searchTerm);
      }
    }

    // Check if any employee matches search (using employees already loaded with approver)
    const employeeMatch = approver.employees?.some(employee => {
      const employeeNameMatch = `${employee.name.first} ${employee.name.last}`.toLowerCase().includes(searchTermLower);
      const employeeIdMatch = employee.altEmployeeID?.toLowerCase().includes(searchTermLower);
      return employeeNameMatch || employeeIdMatch;
    }) || false;

    return approverMatch || employeeMatch;
  }) || [];

  // Helper function to get display name for approver
  const getApproverDisplayName = (approver: Approver): string => {
    if (approver.type === "approvalGroup") {
      return approver.approvalGroupName || "Unknown Group";
    } else if (approver.type === "manager" && approver.name) {
      return `${approver.name.last} - ${approver.name.first} ${approver.altEmployeeID || approver.employeeID || ''}`;
    }
    return "Unknown Approver";
  };


  const shouldShowEmployees = searchTerm.length > 0;

  // Check if location filter should be shown (only for Admin, Payroll, or HR roles)
  const shouldShowLocationFilter = (userRole.admin || userRole.payroll || userRole.hr) &&
                                   (type === TimesheetType.Unsubmitted || 
                                    type === TimesheetType.SuperApproved || 
                                    type === TimesheetType.PayApproved ||
                                    type === TimesheetType.Submitted ||
                                    type === TimesheetType.ManagerPending);

  return (
    <PageWrapper title={translator.translate("supervisorList")} backArrow={true} backgroundColor={Colors.White}>
      {/* Location filter dropdown and Search bar on same row */}
      <div style={{ display: 'flex', alignItems: 'center', marginBottom: '10px', position: 'relative', width: '100%' }}>
        {/* Location filter dropdown - only show for Unsubmitted, SuperApproved, or PayApproved */}
        {shouldShowLocationFilter && (
          <div className="employee-view-sort-by" style={{ marginRight: '20px' }}>
            <label>Location: </label>
            <select value={selectedLocation} onChange={(e) => handleLocationChange(e.target.value)}>
              <option key={0} value={"ALL"}>
                ALL
              </option>
              {locations?.filter(location => location.testLocation === false).sort((a, b) => {
                // Sort by altLocationID if available, otherwise by name
                const aKey = a.altLocationID || a.name;
                const bKey = b.altLocationID || b.name;
                return aKey.localeCompare(bKey);
              }).map((location) => (
                <option key={location.locationID} value={location.locationID.toString()}>
                  {location.altLocationID ? `${location.altLocationID} - ${location.name}` : location.name}
                </option>
              ))}
            </select>
          </div>
        )}
        
        {/* Search bar - centered */}
        <div style={{ flex: 1, display: 'flex', justifyContent: 'center' }}>
          <SearchBar
            placeholder={"Search Timesheets"}
            value={searchTerm}
            onChange={setSearchTerm}
            style={{width: type === 'unsubmitted' ? '100%' : '75%', maxWidth: '600px'}}
          />
        </div>
        
        {/* Spacer to balance the Location dropdown on the left */}
        {shouldShowLocationFilter && (
          <div style={{ width: '200px', flexShrink: 0 }}></div>
        )}
      </div>

      {/* Push notification reminder button - SendTimesheetReminders permission (super admin implies it) + company config, unsubmitted type only */}
      {can(userRole, 'sendTimesheetReminders') && type === TimesheetType.Unsubmitted && config.showPushNotificationButton && (
        <div style={{ display: 'flex', justifyContent: 'end', width: '100%', margin: '8px 0' }}>
          <ActionButton
            title={"Send Push Notification Reminder"}
            isEnabled={!pushNotificationSent}
            onAction={handlePushNotificationReminder}
          />
        </div>
      )}

      {/* Show loading message or fetched data */}
      {data ? (
        <div>
          {/* Top actions row */}
          {(type === TimesheetType.PayApproved && config.printAllTimesheets && (userRole.admin || userRole.payroll)) && (
            <div style={{ display: 'flex', justifyContent: 'flex-end', width: '100%', margin: '8px 0' }}>
              <ActionButton
                title={"Print All Timesheets"}
                isEnabled={!isDownloading}
                onAction={handleDownloadAll}
              />
            </div>
          )}
          {(type === TimesheetType.PayApproved && config.showSageExport && (userRole.admin || userRole.payroll)) && (
            <div style={{ display: 'flex', justifyContent: 'flex-end', width: '100%', margin: '8px 0' }}>
              <ActionButton
                title={"Generate Sage Export Excel"}
                isEnabled={!isSageExporting}
                onAction={handleSageExport}
              />
            </div>
          )}
          {(type === TimesheetType.PayApproved && config.showSageExport && (userRole.admin || userRole.payroll)) && (
            <div style={{ display: 'flex', justifyContent: 'flex-end', width: '100%', margin: '8px 0' }}>
              <ActionButton
                title={"Export to Sage API"}
                isEnabled={!isPushingToSage}
                onAction={handleSagePush}
              />
            </div>
          )}
          {(type === TimesheetType.PayApproved && config.showPayrollApprovedSummaryReport && can(userRole, 'exportPayroll')) && (
            <div style={{ display: 'flex', justifyContent: 'flex-end', width: '100%', margin: '8px 0' }}>
              <ActionButton
                title={"Download Payroll Summary Report"}
                isEnabled={!isSummaryDownloading}
                onAction={handlePayrollSummaryDownload}
              />
            </div>
          )}
          {/* Handle ManagerPending type with approvers */}
          {type === TimesheetType.ManagerPending ? (
            <>
              {/* Check if there are any approvers in the fetched data */}
              {filteredApprovers.length > 0 ? (
                // Map over the list of approvers and render each one
                filteredApprovers.map((approver: Approver, index: number) => {
                  return (
                    <div key={`${approver.type}-${approver.type === 'approvalGroup' ? approver.approvalGroupID : approver.employeeID}-${index}`} className="supervisor-container">
                      <div
                        className="supervisorList-card"
                        onClick={() => handleApproverClick(approver)}
                      >
                        {/* Display approver's name */}
                        <h4>{getApproverDisplayName(approver)}</h4>
                        {/* Display count in the center */}
                        <div className="count-container">
                          <p><strong>{translator.translate("Count")} </strong>{approver.count}</p>
                        </div>
                      </div>
                      
                      {shouldShowEmployees && (
                        <div className="employee-list">
                          {approver.employees
                            ?.filter(employee => {
                              const searchTermLower = searchTerm.toLowerCase();
                              const employeeNameMatch = `${employee.name.first} ${employee.name.last}`.toLowerCase().includes(searchTermLower);
                              const employeeIdMatch = employee.altEmployeeID?.toLowerCase().includes(searchTermLower);
                              return employeeNameMatch || employeeIdMatch;
                            })
                            .map(employee => (
                              <div 
                                key={employee.altEmployeeID ?? employee.employeeID ?? 0} 
                                className="employee-card"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleEmployeeClick(employee);
                                }}
                              >
                                <div className="employee-info">
                                  <h5>{employee.name.last} - {employee.name.first} {employee.altEmployeeID}</h5>
                                </div>
                              </div>
                            ))
                          }
                        </div>
                      )}
                    </div>
                  );
                })
              ) : (
                <>
                </>
              )}
            </>
          ) : (
            <></>
          )}

          {/* Check if there are any supervisors in the fetched data */}
          {filteredSupervisors.length > 0 ? (
            filteredSupervisors.map((supervisor: SupervisorCount) => (
              <div key={supervisor.altEmployeeID ?? supervisor.employeeID ?? 0} className="supervisor-container"
              onClick={() =>
                handleClick(supervisor.employeeID, `${supervisor.name.last} - ${supervisor.name.first}`, supervisor.altEmployeeID)
              }
              >
                <div className="supervisorList-card">
                  <div className="supervisor-info">
                    <h4>
                      {supervisor.name.last} - {supervisor.name.first} 
                      {supervisor.altEmployeeID && ` ${supervisor.altEmployeeID}`}
                    </h4>
                    <p className="count-text">
                      <strong>{translator.translate("Count")}</strong>
                      {supervisor.count}
                    </p>
                  </div>
                  <div className="action-button-container" onClick={(e) => e.stopPropagation()}>
                    {type === TimesheetType.PayApproved && (
                      <ActionButton
                        title={"Print"}
                        isEnabled={!isDownloading}
                        onAction={() => handleDownload(supervisor.employeeID, supervisor.altEmployeeID)}
                      />
                    )}
                    {config.vistaBatchExport && (userRole.admin || userRole.payroll || userRole.hr) && type === TimesheetType.PayApproved && (
                        <ActionButton
                          title={"Vista Export"}
                          isEnabled={true}
                          onAction={() => handleVistaExport(supervisor.employeeID, `${supervisor.name.last} - ${supervisor.name.first}`, supervisor.altEmployeeID)}
                        />
                      )}
                    {config.vistaBatchExport && (userRole.admin || userRole.payroll || userRole.hr) && type === TimesheetType.PayApproved && (
                        <ActionButton
                          title={"Vista Export V2"}
                          isEnabled={true}
                          onAction={() => handleVistaExportV2(supervisor.employeeID, `${supervisor.name.last} - ${supervisor.name.first}`, supervisor.altEmployeeID)}
                        />
                      )}
                  </div>
                </div>
                
                {shouldShowEmployees && (
                  <div className="employee-list">
                    {supervisor.employees
                      .filter(employee => {
                        const searchTermLower = searchTerm.toLowerCase();
                        const employeeNameMatch = `${employee.name.first} ${employee.name.last}`.toLowerCase().includes(searchTermLower);
                        const employeeIdMatch = employee.altEmployeeID?.toLowerCase().includes(searchTermLower);
                        return employeeNameMatch || employeeIdMatch;
                      })
                      .map(employee => (
                        <div 
                          key={employee.altEmployeeID ?? employee.employeeID ?? 0} 
                          className="employee-card"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleEmployeeClick(employee);
                          }}
                        >
                          <div className="employee-info">
                            <h5>{employee.name.last} - {employee.name.first} {employee.altEmployeeID}</h5>
                          </div>
                        </div>
                      ))
                    }
                  </div>
                )}
              </div>
            ))
          ) : (
            <>
              {/* Handle other types with supervisors */}
              {filteredSupervisors.length > 0 ? (
                // Map over the list of supervisors and render each one
                filteredSupervisors.map((supervisor: Supervisor) => (
                  <div
                    key={supervisor.altEmployeeID ?? supervisor.employeeID ?? 0}
                    className="supervisorList-card"
                    onClick={() =>
                      handleClick(supervisor.employeeID, `${supervisor.name.last} - ${supervisor.name.first}`, supervisor.altEmployeeID)
                    }
                  >
                    {/* Display supervisor's name, employee ID */}
                    <h4>{supervisor.name.last + " - " + supervisor.name.first + " " + (supervisor.altEmployeeID ?? supervisor.employeeID ?? '')}</h4>
                    {/* Display count in the center */}
                    <div className="count-container">
                      <p><strong>{translator.translate("Count")} </strong>{supervisor.count}</p>
                    </div>
                    <div className="action-button-container" onClick={(e) => e.stopPropagation()}>
                      {type === TimesheetType.PayApproved && (
                        <ActionButton
                          title={"Print"}
                          isEnabled={!isDownloading}
                          onAction={() => handleDownload(supervisor.employeeID, supervisor.altEmployeeID)}
                        />
                      )}
                      {config.vistaBatchExport && (userRole.admin || userRole.payroll || userRole.hr) && type === TimesheetType.PayApproved && (
                        <ActionButton
                          title={"Vista Export"}
                          isEnabled={true}
                          onAction={() => handleVistaExport(supervisor.employeeID, `${supervisor.name.last} - ${supervisor.name.first}`, supervisor.altEmployeeID)}
                        />
                      )}
                      {config.vistaBatchExport && (userRole.admin || userRole.payroll || userRole.hr) && type === TimesheetType.PayApproved && (
                        <ActionButton
                          title={"Vista Export V2"}
                          isEnabled={true}
                          onAction={() => handleVistaExportV2(supervisor.employeeID, `${supervisor.name.last} - ${supervisor.name.first}`, supervisor.altEmployeeID)}
                        />
                      )}
                    </div>
                  </div>
                ))
              ) : (
                <>
                </>
              )}
            </>
          )}
        </div>
      ) : (
        <p className="no-data-message">{translator.translate("NoDataAvailable")}</p>
      ) }
      
      <VistaBatchExportModal
        visible={vistaModalVisible}
        onClose={() => setVistaModalVisible(false)}
        supervisorName={selectedSupervisorName}
        supervisorId={selectedSupervisorId}
        supervisorAltId={selectedSupervisorAltId}
        startDate={startDate}
        endDate={endDate}
        createPopup={createPopup}
      />
      <VistaBatchExportV2Modal
        visible={vistaV2ModalVisible}
        onClose={() => setVistaV2ModalVisible(false)}
        supervisorName={selectedSupervisorName}
        supervisorId={selectedSupervisorId}
        supervisorAltId={selectedSupervisorAltId}
        startDate={startDate}
        endDate={endDate}
        createPopup={createPopup}
      />
      {sagePushConfirmVisible && (
        <div
          style={{
            position: "fixed",
            inset: 0,
            backgroundColor: "rgba(0, 0, 0, 0.35)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            zIndex: 1200,
          }}
        >
          <div
            style={{
              backgroundColor: Colors.White,
              borderRadius: "8px",
              boxShadow: "0 10px 30px rgba(0, 0, 0, 0.25)",
              width: "400px",
              maxWidth: "90vw",
              padding: "24px",
            }}
          >
            <h3 style={{ margin: "0 0 12px 0" }}>Export to Sage API</h3>
            <p style={{ fontSize: "14px", margin: "0 0 20px 0" }}>
              Push all payroll-approved rows for this pay period directly to Sage 100? This cannot be undone.
            </p>
            <div style={{ display: "flex", justifyContent: "flex-end", gap: "10px" }}>
              <button
                onClick={() => setSagePushConfirmVisible(false)}
                style={{
                  padding: "8px 16px",
                  borderRadius: "6px",
                  border: "1px solid #ccc",
                  background: Colors.White,
                  cursor: "pointer",
                  fontSize: "14px",
                }}
              >
                Cancel
              </button>
              <button
                onClick={confirmSagePush}
                style={{
                  padding: "8px 16px",
                  borderRadius: "6px",
                  border: "none",
                  background: config.primaryColor || "#2f80ed",
                  color: Colors.White,
                  cursor: "pointer",
                  fontSize: "14px",
                  fontWeight: 600,
                }}
              >
                Export
              </button>
            </div>
          </div>
        </div>
      )}
      {isPushingToSage && (
        <div
          style={{
            position: "fixed",
            inset: 0,
            backgroundColor: "rgba(0, 0, 0, 0.35)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            zIndex: 1200,
          }}
        >
          <div
            style={{
              backgroundColor: Colors.White,
              borderRadius: "8px",
              boxShadow: "0 10px 30px rgba(0, 0, 0, 0.25)",
              width: "360px",
              maxWidth: "90vw",
              padding: "20px",
            }}
          >
            <h3 style={{ margin: "0 0 8px 0" }}>Exporting to Sage API</h3>
            <div style={{ fontSize: "14px" }}>Please wait while rows are being pushed to Sage...</div>
          </div>
        </div>
      )}
      {isSageExporting && (
        <div
          style={{
            position: "fixed",
            inset: 0,
            backgroundColor: "rgba(0, 0, 0, 0.35)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            zIndex: 1200,
          }}
        >
          <div
            style={{
              backgroundColor: Colors.White,
              borderRadius: "8px",
              boxShadow: "0 10px 30px rgba(0, 0, 0, 0.25)",
              width: "360px",
              maxWidth: "90vw",
              padding: "20px",
            }}
          >
            <h3 style={{ margin: "0 0 8px 0" }}>Generating Sage Export Excel</h3>
            <div style={{ fontSize: "14px" }}>Please wait while the file is being prepared...</div>
          </div>
        </div>
      )}
      {isDownloading && (
        <div
          style={{
            position: "fixed",
            inset: 0,
            backgroundColor: "rgba(0, 0, 0, 0.35)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            zIndex: 1200,
          }}
        >
          <div
            style={{
              backgroundColor: Colors.White,
              borderRadius: "8px",
              boxShadow: "0 10px 30px rgba(0, 0, 0, 0.25)",
              width: "460px",
              maxWidth: "90vw",
              padding: "20px",
            }}
          >
            <h3 style={{ margin: "0 0 12px 0" }}>Printing All Timesheets</h3>
            <div style={{ fontSize: "14px", marginBottom: "8px" }}>{batchAllProgress.currentStep}</div>
            <div style={{ width: "100%", height: "10px", backgroundColor: "#e4e7eb", borderRadius: "5px", overflow: "hidden" }}>
              <div
                style={{
                  width: `${batchAllProgress.progress}%`,
                  height: "100%",
                  backgroundColor: Colors.Primary || "#2f80ed",
                  transition: "width 0.3s ease",
                }}
              />
            </div>
            <div style={{ marginTop: "8px", textAlign: "right", fontSize: "13px" }}>
              {batchAllProgress.progress}%
            </div>
          </div>
        </div>
      )}
      {isSummaryDownloading && (
        <div
          style={{
            position: "fixed",
            inset: 0,
            backgroundColor: "rgba(0, 0, 0, 0.35)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            zIndex: 1200,
          }}
        >
          <div
            style={{
              backgroundColor: Colors.White,
              borderRadius: "8px",
              boxShadow: "0 10px 30px rgba(0, 0, 0, 0.25)",
              width: "460px",
              maxWidth: "90vw",
              padding: "20px",
            }}
          >
            <h3 style={{ margin: "0 0 12px 0" }}>Generating Payroll Summary Report</h3>
            <div style={{ fontSize: "14px", marginBottom: "8px" }}>{summaryProgress.currentStep}</div>
            <div style={{ width: "100%", height: "10px", backgroundColor: "#e4e7eb", borderRadius: "5px", overflow: "hidden" }}>
              <div
                style={{
                  width: `${summaryProgress.progress}%`,
                  height: "100%",
                  backgroundColor: Colors.Primary || "#2f80ed",
                  transition: "width 0.3s ease",
                }}
              />
            </div>
            <div style={{ marginTop: "8px", textAlign: "right", fontSize: "13px" }}>
              {summaryProgress.progress}%
            </div>
          </div>
        </div>
      )}
    </PageWrapper>
  );
};

export default SupervisorListPage;